import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { api, ApiError } from '@/lib/api';
import {
  AlertTriangle, Check, Database, GitCommit, Server, ChevronDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface PostgisHealth {
  srsRowCount: number;
  srid4326Present: boolean;
  extensionVersion: string;
}

interface DbStatsTable {
  name: string;
  rowCount: number;
  sizeBytes: number;
}

interface DbStats {
  tables: DbStatsTable[];
  totalSizeBytes: number;
}

interface MigrationEntry {
  hash: string;
  createdAt: string;
}

interface Migrations {
  applied: MigrationEntry[];
}

interface SystemInfo {
  dbVersion: string;
  dbTimezone: string;
  dbNow: string;
}

function humanBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let i = 0;
  let val = n;
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024;
    i++;
  }
  const fixed = val >= 100 || i === 0 ? Math.round(val) : val.toFixed(1);
  return `${fixed} ${units[i]}`;
}

function unwrap<T>(envelope: { data: T }): T {
  return envelope.data;
}

function FieldRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-[11px] uppercase tracking-[0.12em] text-zinc-500 font-medium">
        {label}
      </span>
      <span className={cn('text-zinc-200', mono && 'font-mono text-xs tabular-nums')}>
        {value}
      </span>
    </div>
  );
}

function PostgisCard() {
  const qc = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data, isLoading, error } = useQuery<PostgisHealth>({
    queryKey: ['admin', 'tools', 'postgis'],
    queryFn: () => api.get<{ data: PostgisHealth }>('/api/admin/tools/postgis').then(unwrap),
  });

  const repopulate = useMutation({
    mutationFn: () => api.post<{ data: { ok: true; srsRowCount: number } }>('/api/admin/tools/postgis/repopulate', {}),
    onSuccess: (res) => {
      const count = res?.data?.srsRowCount ?? 0;
      toast.success(`spatial_ref_sys восстановлена (${count} строк)`);
      qc.invalidateQueries({ queryKey: ['admin', 'tools', 'postgis'] });
      setConfirmOpen(false);
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
      toast.error(`Не удалось восстановить: ${msg}`);
    },
  });

  const isBroken = data && !data.srid4326Present;

  return (
    <Card className={cn(isBroken && 'border-rose-500/40 bg-rose-950/10')}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2">
          {isBroken ? (
            <AlertTriangle className="h-4 w-4 text-rose-400" />
          ) : (
            <Database className="h-4 w-4 text-zinc-500" />
          )}
          PostGIS
        </CardTitle>
        {data ? (
          isBroken ? (
            <Badge variant="destructive">
              <AlertTriangle className="h-3 w-3" />
              BROKEN
            </Badge>
          ) : (
            <Badge variant="success">
              <Check className="h-3 w-3" />
              OK
            </Badge>
          )
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-5 w-24" />
          </div>
        ) : error ? (
          <div className="text-sm text-rose-300">
            {error instanceof ApiError ? `${error.code}: ${error.message}` : String(error)}
          </div>
        ) : data ? (
          <>
            <FieldRow label="SRID 4326" value={data.srid4326Present ? 'present' : 'missing'} />
            <FieldRow label="Строк в spatial_ref_sys" value={data.srsRowCount.toLocaleString('ru')} mono />
            <FieldRow label="Версия расширения" value={data.extensionVersion} mono />
            {isBroken && (
              <Button
                variant="destructive"
                onClick={() => setConfirmOpen(true)}
                disabled={repopulate.isPending}
                className="w-full mt-2"
              >
                Repopulate spatial_ref_sys
              </Button>
            )}
          </>
        ) : null}
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Восстановить spatial_ref_sys?</DialogTitle>
            <DialogDescription>
              Будет загружена spatial_ref_sys.sql из upstream PostGIS и применена
              к базе. Операция может занять до минуты и затронет все таблицы с
              геометрией.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={repopulate.isPending}>
              Отмена
            </Button>
            <Button
              variant="destructive"
              onClick={() => repopulate.mutate()}
              disabled={repopulate.isPending}
            >
              {repopulate.isPending ? 'Восстановление…' : 'Восстановить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function DbStatsCard() {
  const [showAll, setShowAll] = useState(false);
  const { data, isLoading, error } = useQuery<DbStats>({
    queryKey: ['admin', 'tools', 'db-stats'],
    queryFn: () => api.get<{ data: DbStats }>('/api/admin/tools/db/stats').then(unwrap),
  });

  const tables = data?.tables ?? [];
  const visible = showAll ? tables : tables.slice(0, 10);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2">
          <Database className="h-4 w-4 text-zinc-500" />
          База данных
        </CardTitle>
        {data ? (
          <Badge variant="outline" className="font-mono tabular-nums">
            {humanBytes(data.totalSizeBytes)}
          </Badge>
        ) : null}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-5 w-full" />
            ))}
          </div>
        ) : error ? (
          <div className="text-sm text-rose-300">
            {error instanceof ApiError ? `${error.code}: ${error.message}` : String(error)}
          </div>
        ) : (
          <>
            <div className="max-h-[260px] overflow-y-auto rounded-md border border-zinc-800/80">
              <table className="w-full text-sm">
                <thead className="bg-bg-1 text-[10px] uppercase tracking-[0.12em] text-zinc-500 sticky top-0">
                  <tr className="border-b border-zinc-900">
                    <th className="text-left px-3 py-2 font-medium">Таблица</th>
                    <th className="text-right px-3 py-2 font-medium">Строк</th>
                    <th className="text-right px-3 py-2 font-medium">Размер</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((t) => (
                    <tr key={t.name} className="border-t border-zinc-900/80 hover:bg-zinc-900/40">
                      <td className="px-3 py-1.5 font-mono text-xs text-zinc-200">{t.name}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-xs tabular-nums text-zinc-300">
                        {t.rowCount.toLocaleString('ru')}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono text-xs tabular-nums text-zinc-300">
                        {humanBytes(t.sizeBytes)}
                      </td>
                    </tr>
                  ))}
                  {visible.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-3 py-3 text-center text-zinc-500">
                        Нет данных
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            {tables.length > 10 && (
              <div className="pt-2 flex justify-center">
                <Button variant="ghost" size="sm" onClick={() => setShowAll((v) => !v)}>
                  <ChevronDown className={cn('h-3 w-3 transition-transform', showAll && 'rotate-180')} />
                  {showAll ? 'Свернуть' : `Показать все (${tables.length})`}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function MigrationsCard() {
  const { data, isLoading, error } = useQuery<Migrations>({
    queryKey: ['admin', 'tools', 'migrations'],
    queryFn: () => api.get<{ data: Migrations }>('/api/admin/tools/migrations').then(unwrap),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GitCommit className="h-4 w-4 text-zinc-500" />
          Миграции
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-4 w-full" />
            ))}
          </div>
        ) : error ? (
          <div className="text-sm text-rose-300">
            {error instanceof ApiError ? `${error.code}: ${error.message}` : String(error)}
          </div>
        ) : (
          <div className="max-h-[240px] overflow-y-auto">
            <ol className="relative space-y-2 pl-4">
              <div className="absolute left-[3px] top-1.5 bottom-1.5 w-px bg-zinc-800" aria-hidden />
              {(data?.applied ?? []).map((m, idx) => (
                <li
                  key={`${m.hash}-${idx}`}
                  className="relative flex items-center justify-between text-xs"
                >
                  <span
                    className="absolute -left-4 top-1 h-1.5 w-1.5 rounded-full bg-zinc-700 ring-2 ring-bg-2"
                    aria-hidden
                  />
                  <span
                    className="font-mono text-zinc-300 truncate max-w-[10rem]"
                    title={m.hash}
                  >
                    {m.hash.slice(0, 12)}…
                  </span>
                  <span className="font-mono text-zinc-500 tabular-nums">
                    {(() => {
                      try {
                        return new Date(m.createdAt).toLocaleString('ru');
                      } catch {
                        return m.createdAt;
                      }
                    })()}
                  </span>
                </li>
              ))}
            </ol>
            {(data?.applied ?? []).length === 0 ? (
              <div className="text-zinc-500 text-sm">Нет применённых миграций</div>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SystemCard() {
  const { data, isLoading, error } = useQuery<SystemInfo>({
    queryKey: ['admin', 'tools', 'system'],
    queryFn: () => api.get<{ data: SystemInfo }>('/api/admin/tools/system').then(unwrap),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Server className="h-4 w-4 text-zinc-500" />
          Система
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ) : error ? (
          <div className="text-sm text-rose-300">
            {error instanceof ApiError ? `${error.code}: ${error.message}` : String(error)}
          </div>
        ) : data ? (
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-medium">DB version</dt>
              <dd
                className="text-zinc-200 font-mono text-xs mt-1 line-clamp-2"
                title={data.dbVersion}
              >
                {data.dbVersion}
              </dd>
            </div>
            <div>
              <dt className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-medium">Timezone</dt>
              <dd className="text-zinc-200 font-mono text-xs mt-1">{data.dbTimezone}</dd>
            </div>
            <div>
              <dt className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-medium">now()</dt>
              <dd className="text-zinc-200 font-mono text-xs mt-1 tabular-nums">{data.dbNow}</dd>
            </div>
          </dl>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function ToolsPage() {
  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <h1 className="text-2xl font-semibold tracking-[-0.01em] text-zinc-50">Инструменты</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PostgisCard />
        <DbStatsCard />
        <MigrationsCard />
        <SystemCard />
      </div>
    </div>
  );
}
